import { z } from 'zod'
import type { Language } from './schemas/simulation'
import type { SampleOrderStatus } from './schemas/sample-orders'
import type { TastingSource } from './schemas/tastings'

/**
 * 검증 단계 (대표 Top5 #3, 2026-10-30. 명세 P1-03 '제품 준비 상태는 충족한 조건과 부족한 조건으로 설명한다').
 * 아이디어 → 배합 확정 → 시제품(샘플) → 시식 → 시험 생산 → 반복 생산. 점수를 매기지 않고, 단계마다 조건이 채워졌는지만 본다.
 * 지금 단계 = 앞에서부터 조건을 다 채운 마지막 단계 (중간 단계가 비면 그 뒤 기록이 있어도 넘어가지 않는다 — 보수적으로).
 * 판정 재료는 서버가 실제 기록에서 모은다(apps/api/src/lib/validation-stage.ts): 배합 버전·샘플 주문·시식 기록·견적.
 * AI 결과만으로는 '아이디어'를 넘지 않는다 — 배합 확정은 사람이 저장한 버전이 있어야 한다.
 */
export const VALIDATION_STAGES = [
  'idea',
  'formula_set',
  'sample',
  'tasting',
  'pilot_run',
  'repeat_production',
] as const
export type ValidationStage = (typeof VALIDATION_STAGES)[number]
/** 서버가 준 단계 글자가 아는 값인지 (새 단계를 모르는 앱은 그리지 않는다) */
export function isValidationStage(value: unknown): value is ValidationStage {
  return (VALIDATION_STAGES as readonly unknown[]).includes(value)
}

/** 단계 판정에 쓰는 실제 기록 (서버가 DB 에서 센다) */
export interface ValidationFacts {
  /** 결과(배합·원가)가 다 만들어졌는지 */
  resultReady: boolean
  /** 사람이 저장한 배합 버전 수 (simulation_versions 의 save·restore, C42). 기준선(baseline)은 세지 않는다 */
  savedVersions: number
  /** 이 제품을 기준 버전으로 고정해 낸 견적이 있는지 (C43: 견적 접수 때 배합 버전을 고정한다) */
  quoteLockedVersion: boolean
  /** 이 제품의 샘플 주문 상태들 (AI 중계 샘플) */
  sampleOrders: readonly SampleOrderStatus[]
  /** 이 제품의 시식 기록 출처들 (한 기록 = 한 칸) */
  tastings: readonly TastingSource[]
  /** 견적에서 공장을 골랐는지 (quote_requests.selected_offer_id) */
  factoryChosen: boolean
}

export const VALIDATION_CONDITIONS = [
  'result_ready',
  'formula_saved',
  'sample_received',
  'tasting_recorded',
  'factory_chosen',
  'first_production_tasted',
  'second_production_tasted',
] as const
export type ValidationCondition = (typeof VALIDATION_CONDITIONS)[number]

/** 단계마다 채워야 하는 조건 (앞 단계 조건은 다시 적지 않는다) */
const STAGE_CONDITIONS: Record<ValidationStage, readonly ValidationCondition[]> = {
  idea: [],
  formula_set: ['result_ready', 'formula_saved'],
  sample: ['sample_received'],
  tasting: ['tasting_recorded'],
  pilot_run: ['factory_chosen', 'first_production_tasted'],
  repeat_production: ['second_production_tasted'],
}

/** 이 단계에 들어가려면 채울 조건 (단계 칸을 누르면 앱이 안내로 보여 준다) */
export function stageConditions(stage: ValidationStage): readonly ValidationCondition[] {
  return STAGE_CONDITIONS[stage]
}

/** 샘플을 받았다고 보는 주문 상태 (공장이 보냈고 고객이 받았거나 시식까지 마친 것) */
const SAMPLE_RECEIVED: readonly SampleOrderStatus[] = ['delivered', 'completed']

function conditionMet(condition: ValidationCondition, facts: ValidationFacts): boolean {
  const firstProduction = facts.tastings.filter((s) => s === 'first_production').length
  switch (condition) {
    case 'result_ready':
      return facts.resultReady
    case 'formula_saved':
      return facts.savedVersions > 0 || facts.quoteLockedVersion
    case 'sample_received':
      // 직접 만든 시제품을 맛본 기록도 시제품이 있었다는 증거다
      return (
        facts.sampleOrders.some((s) => SAMPLE_RECEIVED.includes(s)) ||
        facts.tastings.some((s) => s === 'sample' || s === 'self_made')
      )
    case 'tasting_recorded':
      return facts.tastings.length > 0
    case 'factory_chosen':
      return facts.factoryChosen
    case 'first_production_tasted':
      return firstProduction >= 1
    case 'second_production_tasted':
      // 첫 생산분 시식이 두 번(두 회차) 이상이면 같은 배합을 다시 생산했다고 본다
      return firstProduction >= 2
  }
}

export interface ValidationStageView {
  /** 지금 단계 */
  stage: ValidationStage
  /** 단계마다 조건과 충족 여부. 화면이 단계 칸을 그리고, 다음 단계의 부족한 조건을 보인다 */
  steps: { stage: ValidationStage; conditions: { code: ValidationCondition; met: boolean }[] }[]
  /** 다음 단계로 가려면 채울 조건 (마지막 단계면 빈 목록) */
  missing: ValidationCondition[]
}

/** GET /simulations/:id/readiness 응답의 readiness (앱이 이 스키마로 읽는다) */
export const validationStageViewSchema = z.object({
  stage: z.enum(VALIDATION_STAGES),
  steps: z
    .array(
      z.object({
        stage: z.enum(VALIDATION_STAGES),
        conditions: z.array(z.object({ code: z.enum(VALIDATION_CONDITIONS), met: z.boolean() })),
      }),
    )
    .max(VALIDATION_STAGES.length),
  missing: z.array(z.enum(VALIDATION_CONDITIONS)),
})

/** 실제 기록으로 지금 단계를 판정한다. 같은 기록이면 같은 답 (점수·확률 없음) */
export function judgeValidationStage(facts: ValidationFacts): ValidationStageView {
  const steps = VALIDATION_STAGES.map((stage) => ({
    stage,
    conditions: STAGE_CONDITIONS[stage].map((code) => ({ code, met: conditionMet(code, facts) })),
  }))
  let reached = 0
  while (reached + 1 < steps.length && steps[reached + 1]!.conditions.every((c) => c.met))
    reached += 1
  const next = steps[reached + 1]
  return {
    stage: steps[reached]!.stage,
    steps,
    missing: next ? next.conditions.filter((c) => !c.met).map((c) => c.code) : [],
  }
}

export const VALIDATION_STAGE_COPY: Record<
  Language,
  {
    title: string
    stage: Record<ValidationStage, string>
    condition: Record<ValidationCondition, string>
    next: string
    done: string
  }
> = {
  ko: {
    title: '검증 단계',
    stage: {
      idea: '아이디어',
      formula_set: '배합 확정',
      sample: '시제품',
      tasting: '시식',
      pilot_run: '시험 생산',
      repeat_production: '반복 생산',
    },
    condition: {
      result_ready: '배합·원가 결과가 있음',
      formula_saved: '배합을 버전으로 저장함',
      sample_received: '시제품(샘플)을 받거나 직접 만듦',
      tasting_recorded: '시식 기록을 남김',
      factory_chosen: '견적에서 공장을 고름',
      first_production_tasted: '첫 생산분 시식 기록',
      second_production_tasted: '두 번째 생산분 시식 기록',
    },
    next: '다음 단계에 필요한 것',
    done: '모든 단계를 마쳤어요',
  },
  en: {
    title: 'Validation stage',
    stage: {
      idea: 'Idea',
      formula_set: 'Formula set',
      sample: 'Prototype',
      tasting: 'Tasting',
      pilot_run: 'Pilot run',
      repeat_production: 'Repeat production',
    },
    condition: {
      result_ready: 'Formula and cost result exist',
      formula_saved: 'Formula saved as a version',
      sample_received: 'Prototype received or made yourself',
      tasting_recorded: 'Tasting recorded',
      factory_chosen: 'Factory chosen from a quote',
      first_production_tasted: 'First production run tasted',
      second_production_tasted: 'Second production run tasted',
    },
    next: 'Needed for the next stage',
    done: 'Every stage is recorded',
  },
  ja: {
    title: '検証段階',
    stage: {
      idea: 'アイデア',
      formula_set: '配合確定',
      sample: '試作品',
      tasting: '試食',
      pilot_run: '試験生産',
      repeat_production: '繰り返し生産',
    },
    condition: {
      result_ready: '配合・原価の結果がある',
      formula_saved: '配合をバージョンとして保存した',
      sample_received: '試作品(サンプル)を受け取った、または自作した',
      tasting_recorded: '試食記録がある',
      factory_chosen: '見積から工場を選んだ',
      first_production_tasted: '初回生産品の試食記録',
      second_production_tasted: '2回目の生産品の試食記録',
    },
    next: '次の段階に必要なこと',
    done: 'すべての段階を記録しました',
  },
  zh: {
    title: '验证阶段',
    stage: {
      idea: '创意',
      formula_set: '配方确定',
      sample: '样品',
      tasting: '试吃',
      pilot_run: '试生产',
      repeat_production: '重复生产',
    },
    condition: {
      result_ready: '已有配方和成本结果',
      formula_saved: '已将配方保存为版本',
      sample_received: '已收到样品或自制样品',
      tasting_recorded: '已有试吃记录',
      factory_chosen: '已在报价中选定工厂',
      first_production_tasted: '首批生产品试吃记录',
      second_production_tasted: '第二批生产品试吃记录',
    },
    next: '进入下一阶段需要',
    done: '所有阶段均已记录',
  },
}
